'use client';

import Grid from '@enlighten/design-system/dist/Grid';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Tabs from '@enlighten/design-system/dist/Tabs';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { PropsWithChildren } from 'react';

const terms = [
	{
		id: '1',
		title: '서비스 이용약관',
		linkProps: {
			href: '/terms/1',
		},
	},
	{
		id: '6',
		title: '안전보건경영방침',
		linkProps: {
			href: '/terms/6',
		},
	},
	{
		id: '2',
		title: '개인정보 처리 방침',
		linkProps: {
			href: 'https://privacy.enlighten.kr/b950f03f-3434-4c34-8a67-13657d724408',
			target: '_blank',
		},
	},
	{
		id: '3',
		title: '개인정보 수집 및 이용 안내',
		linkProps: {
			href: 'https://privacy.enlighten.kr/107ce152-8c75-40fd-b814-22bc19ba485b',
			target: '_blank',
		},
	},
	{
		id: '4',
		title: '마케팅 정보 보호',
		linkProps: {
			href: 'https://privacy.enlighten.kr/1f1ddbfe-02cc-47b9-9534-114b050cd610',
			target: '_blank',
		},
	},
	{
		id: '5',
		title: '개인정보 제3자 제공동의',
		linkProps: {
			href: 'https://privacy.enlighten.kr/b8f4773a-1c80-4d63-9a8d-c8f0ab2555e0',
			target: '_blank',
		},
	},
];

export default function Layout({ children }: PropsWithChildren) {
	const [, , id] = usePathname().split('/');

	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	return (
		<Grid container direction="column" alignItems="center" maxWidth={1024} minHeight={1223} marginX="auto" paddingY="15px">
			<Tabs
				orientation={isMobile ? 'vertical' : 'horizontal'}
				value={id}
				indicatorColor="primary"
				items={terms.map((term) => ({
					disableRipple: true,
					label: (
						<Link {...term.linkProps}>
							<Typography fontSize={16} fontWeight={700}>
								{term.title}
							</Typography>
						</Link>
					),
					value: term.id,
					sx: {
						width: { xs: '100%', md: 'auto' },
						maxWidth: '100%',
						padding: 0,
						'& a': {
							marginX: 2,
							padding: '12px 0',
						},
						'.MuiTypography-root': {
							color: term.id === id ? 'primary.main' : 'common.black',
							textShadow: term.id === id ? '0 0 .25px currentcolor' : 'none',
							transition: 'all 0.3s ease',
						},
						':hover .MuiTypography-root': {
							color: 'primary.light',
						},
					},
				}))}
				sx={{
					width: { xs: '100%', md: 'auto' },
					'& a': {
						display: 'flex',
						justifyContent: 'center',
					},
				}}
			/>
			<Grid
				sx={(theme) => ({
					lineHeight: 1.5,
					padding: { xs: '24px 10px 10px 34px', md: '24px 10px 10px 10px' },
					'ul, ol': {
						listStyleType: 'none',
					},
					'ol, dl': {
						marginTop: 0,
					},
					dd: {
						margin: 0,
					},
					'& p': {
						margin: 0,
					},
					'.mb16': {
						marginBottom: 2,
					},
					table: {
						width: '100%',
						borderTop: `1px solid ${theme.palette.grey['100']}`,
						borderLeft: `1px solid ${theme.palette.grey['100']}`,
						th: {
							bgcolor: 'grey.100',
							padding: '15px 13px',
						},
						td: {
							padding: '14px 15px',
						},
						'th, td': {
							textAlign: 'center',
							borderRight: `1px solid ${theme.palette.grey['100']}`,
							borderBottom: `1px solid ${theme.palette.grey['100']}`,
						},
					},
				})}
			>
				{children}
			</Grid>
		</Grid>
	);
}
